import React, { useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { updateQueue } from 'flow/actions/pbx-actions';
import {
    selectQueueDescription,
    selectQueueEditable,
    selectQueueKeywords,
} from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { PbxParams } from 'flow/types/Params';
import { State } from 'flow/types/State';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';

import Page from '../Setting/Page';
import BaseInput from '../ui-components/BaseInput';
import Loading from '../ui-components/Loading';

const NAME_INPUT_ID = 'pbx-settings-name';
const KEYWORDS_INPUT_ID = 'pbx-settings-keywords';

/**
 * Name and keywords are edited on one page, which is what PD-2455 asks for: "move the
 * field for search word/keywords into the area of editing of the queue name".
 *
 * The name is the queue's own `description`, while keywords sit on the PBX service's
 * contact. Both save together in one updateQueue call, and only the fields that changed
 * are sent.
 */
const PbxSettingsEditName = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { entityKey } = useParams<PbxParams>();

    const description = useAppSelector((state: State) =>
        selectQueueDescription(state, entityKey as QueueKey),
    );
    const keywords = useAppSelector((state: State) =>
        selectQueueKeywords(state, entityKey as QueueKey),
    );
    const editable = useAppSelector((state: State) =>
        selectQueueEditable(state, entityKey as QueueKey),
    );

    const [newName, setNewName] = useState<string>(null);
    const [newKeywords, setNewKeywords] = useState<string>(null);

    const onClickBack = () => {
        const keywordsChanged =
            newKeywords !== null && newKeywords !== (keywords ?? '');

        // Name and keywords go in one call, to the endpoint that already saves the name.
        // Keywords live on the service's own contact, but neither contacts endpoint can
        // write it: a queue account is not an "anumber account colleague", so the contact
        // never resolves as bound and both reject it. updateQueue applies contact
        // keywords when sent, so the two fields stay together. (AC-1942)
        const nameChanged = !!newName && newName !== description;

        if (nameChanged || keywordsChanged) {
            dispatch(
                updateQueue(entityKey, {
                    ...(nameChanged ? { description: newName } : {}),
                    ...(keywordsChanged
                        ? { contact: { keywords: newKeywords } }
                        : {}),
                }),
            );
        }

        history.push(
            generatePath(routes['pbx.queues.entityKey.settings'], {
                entityKey,
            }),
        );
    };

    // `editable` and the contact's keywords arrive together, from the single-queue fetch
    // PbxQueueRoutes starts on mount; the queue list does not carry either. Until it lands
    // the user's rights are unknown, and the inputs are uncontrolled, so they would keep
    // whatever they first rendered with.
    if (editable === undefined) {
        return <Loading />;
    }

    // The settings list already hides the row for a service the user may not edit, but
    // the page is reachable by URL, so the flag is checked here too. (PD-2455)
    if (!editable) {
        return (
            <Redirect
                to={generatePath(routes['pbx.queues.entityKey.settings'], {
                    entityKey,
                })}
            />
        );
    }

    return (
        <Page header="pbx.name" onClickBack={onClickBack}>
            <div className="flex w-full flex-col gap-y-6">
                <BaseInput
                    id={NAME_INPUT_ID}
                    label={i18next.t('pbx.name')}
                    defaultValue={description}
                    onChange={(e) => setNewName(e.target.value)}
                />

                <BaseInput
                    id={KEYWORDS_INPUT_ID}
                    label={i18next.t('pbx.keywords')}
                    defaultValue={keywords ?? ''}
                    onChange={(e) => setNewKeywords(e.target.value)}
                />
            </div>
        </Page>
    );
};

export default PbxSettingsEditName;
